import {
  type Affix,
  type AffixId,
  AFFIX_IDS,
  equipmentBonus,
  getItem,
  type InventoryState,
  type ItemInstance,
  type ItemTemplate,
} from '@shefira/shared';

/**
 * A piece set against the one worn in its slot, for a card that asks « is it
 * better? » (the merchant's, the bag's). Display only: the stats themselves
 * are the server's, and both sides are read through `equipmentBonus` at their
 * own level with their own affixes (ADR 0057), so a +3 worn piece counts at
 * +3 and a piece for sale at +0.
 */

export type ComparedStat = 'dps' | 'defence' | 'health';

export interface StatDelta {
  stat: ComparedStat;
  /** Candidate minus worn; dps in tenths already rounded. */
  delta: number;
}

/** One of the fight's affixes (ADR 0057), the three stats aside: candidate minus worn. */
export interface AffixDelta {
  id: AffixId;
  delta: number;
}

/** Already counted in the stats' deltas through `equipmentBonus`. */
const STAT_AFFIXES: ReadonlySet<AffixId> = new Set<AffixId>(['dps', 'health', 'defence']);

function affixDeltas(
  mine: readonly Affix[] | undefined,
  theirs: readonly Affix[] | undefined,
): AffixDelta[] {
  const value = (list: readonly Affix[] | undefined, id: AffixId): number =>
    list?.find((affix) => affix.id === id)?.value ?? 0;
  return AFFIX_IDS.filter((id) => !STAT_AFFIXES.has(id))
    .map((id) => ({ id, delta: Math.round((value(mine, id) - value(theirs, id)) * 10) / 10 }))
    .filter((entry) => entry.delta !== 0);
}

export interface Comparison {
  /** The worn piece, or null when the slot is empty. */
  worn: { template: ItemTemplate; upgradeLevel: number } | null;
  /** Every stat either side gives, in the card's order; a zero stays, it says « same ». */
  deltas: StatDelta[];
  /** The other affixes that differ, gained (positive) or lost (negative). */
  affixes: AffixDelta[];
}

const STATS: readonly ComparedStat[] = ['dps', 'defence', 'health'];

/** What wearing `candidate` at `level` would change against what `equipped` holds; undefined when there is nothing to compare. */
export function compareWithWorn(
  candidate: ItemTemplate,
  equipped: InventoryState['equipped'] | undefined,
  level = 0,
  affixes?: readonly Affix[],
): Comparison | undefined {
  if (candidate.kind !== 'equipment' || candidate.cosmetic || candidate.slot === undefined) {
    return undefined;
  }
  // A pet or a mount gives nothing to weigh: it is chosen for itself.
  // Wings and trails are only ever a look (ADR 0053, 0073), already set aside above.
  if (
    candidate.slot === 'pet' ||
    candidate.slot === 'mount' ||
    candidate.slot === 'wings' ||
    candidate.slot === 'trail'
  ) {
    return undefined;
  }
  const instance: ItemInstance | undefined = equipped?.[candidate.slot];
  const template = instance === undefined ? undefined : getItem(instance.templateId);
  const worn =
    instance === undefined || template === undefined
      ? null
      : { template, upgradeLevel: instance.upgradeLevel };
  const mine = equipmentBonus([{ template: candidate, upgradeLevel: level, affixes }]);
  const theirs =
    worn === null || instance === undefined
      ? { dps: 0, defence: 0, health: 0 }
      : equipmentBonus([{ ...worn, affixes: instance.affixes }]);
  const deltas = STATS.filter((stat) => mine[stat] !== 0 || theirs[stat] !== 0).map((stat) => ({
    stat,
    // Tenths of dps subtracted as integers: 2.3 - 2.1 must not print 0.19999.
    delta:
      stat === 'dps' ? Math.round((mine.dps - theirs.dps) * 10) / 10 : mine[stat] - theirs[stat],
  }));
  return { worn, deltas, affixes: affixDeltas(affixes, instance?.affixes) };
}
